/**
 * <JobCompetencySection /> — one of a Job's five nested detail
 * collections (M21.7): Skills, KPIs, Certificates, Tests, Objectives.
 *
 * All five are rows in the polymorphic `job_competency` table
 * discriminated by `kind`; this component renders ONE kind. The
 * column set shown adapts to the kind:
 *   - skill      → name + level + importance + (KPI mapping = description)
 *   - kpi        → name + behavioral description
 *   - certificate→ name + requirement description
 *   - test       → name + requirement description
 *   - objective  → name + weight (%) + measurement
 *
 * Each row is inline-editable via <EditableField>; an "Add" row form
 * appends a new competency; per-row trash deletes it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "../lib/api";
import type { JobCompetency } from "../lib/api";
import { EditableField } from "./ui/EditableField";

type Kind = JobCompetency["kind"];

const _KIND_LABEL_KEY: Record<Kind, string> = {
  skill: "app.jcs_kind_skill",
  kpi: "app.jcs_kind_kpi",
  certificate: "app.jcs_kind_certificate",
  test: "app.jcs_kind_test",
  objective: "app.jcs_kind_objective",
};

const _KIND_LABEL_LOWER_KEY: Record<Kind, string> = {
  skill: "app.jcs_kind_skill_lower",
  kpi: "app.jcs_kind_kpi_lower",
  certificate: "app.jcs_kind_certificate_lower",
  test: "app.jcs_kind_test_lower",
  objective: "app.jcs_kind_objective_lower",
};

const _KIND_LABEL_SINGULAR_KEY: Record<Kind, string> = {
  skill: "app.jcs_kind_skill_singular",
  kpi: "app.jcs_kind_kpi_singular",
  certificate: "app.jcs_kind_certificate_singular",
  test: "app.jcs_kind_test_singular",
  objective: "app.jcs_kind_objective_singular",
};

export function JobCompetencySection({
  jobId,
  kind,
}: {
  jobId: string;
  kind: Kind;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const _LEVELS = [
    { value: "beginner", label: t("app.jcs_level_beginner") },
    { value: "intermediate", label: t("app.jcs_level_intermediate") },
    { value: "advanced", label: t("app.jcs_level_advanced") },
    { value: "expert", label: t("app.jcs_level_expert") },
  ];
  const _IMPORTANCE = [
    { value: "required", label: t("app.jcs_importance_required") },
    { value: "preferred", label: t("app.jcs_importance_preferred") },
    { value: "nice_to_have", label: t("app.jcs_importance_nice") },
  ];

  const q = useQuery({
    queryKey: ["job-competencies", jobId, kind],
    queryFn: () => api.listJobCompetencies(jobId, kind),
    enabled: !!jobId,
  });

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["job-competencies", jobId, kind],
    });

  const createM = useMutation({
    mutationFn: (name: string) =>
      api.createJobCompetency(jobId, { kind, name_en: name }),
    onSuccess: () => {
      setDraftName("");
      setAdding(false);
      setAddError(null);
      invalidate();
    },
    onError: (e: unknown) =>
      setAddError(e instanceof ApiError ? e.detail : String(e)),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteJobCompetency(id),
    onSuccess: invalidate,
  });

  const patch = (id: string, body: Partial<JobCompetency>) =>
    api.updateJobCompetency(id, body).then((updated) => {
      invalidate();
      return updated;
    });

  const rows = q.data ?? [];
  const kindLabel = t(_KIND_LABEL_KEY[kind]);
  const kindLabelLower = t(_KIND_LABEL_LOWER_KEY[kind]);
  const kindSingular = t(_KIND_LABEL_SINGULAR_KEY[kind]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          {kindLabel} ({rows.length})
        </h2>
        {!adding && (
          <button
            type="button"
            onClick={() => {
              setAddError(null);
              setAdding(true);
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            <Plus className="h-3.5 w-3.5" /> {t("app.jcs_add_short")}
          </button>
        )}
      </div>

      {adding && (
        <div className="mb-4 flex flex-col gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
          <input
            autoFocus
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && draftName.trim())
                createM.mutate(draftName.trim());
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder={t("app.jcs_new_placeholder", { kind: kindSingular })}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          {addError && (
            <p className="text-xs text-rose-700">{addError}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setAddError(null);
              }}
              className="text-xs text-slate-600 hover:text-slate-900"
            >
              {t("app.jcs_cancel")}
            </button>
            <button
              type="button"
              disabled={!draftName.trim() || createM.isPending}
              onClick={() => createM.mutate(draftName.trim())}
              className="rounded bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {createM.isPending ? t("app.jcs_adding_short") : t("app.jcs_add_short")}
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 && !adding ? (
        <p className="text-sm italic text-slate-500">
          {t("app.jcs_no_items_kind", { label: kindLabelLower })}
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-slate-100">
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-start gap-4 py-3 first:pt-0"
            >
              <div className="grid flex-1 gap-3 md:grid-cols-2">
                <EditableField
                  label={t("app.jcs_field_name")}
                  value={row.name_en}
                  onSave={(v) => patch(row.id, { name_en: v as string })}
                />
                {kind === "skill" && (
                  <>
                    <EditableField
                      label={t("app.jcs_field_level")}
                      value={row.level}
                      kind="select"
                      options={_LEVELS}
                      onSave={(v) =>
                        patch(row.id, { level: v as string | null })
                      }
                    />
                    <EditableField
                      label={t("app.jcs_field_importance")}
                      value={row.importance}
                      kind="select"
                      options={_IMPORTANCE}
                      onSave={(v) =>
                        patch(row.id, {
                          importance: v as string | null,
                        })
                      }
                    />
                    <EditableField
                      label={t("app.jcs_field_kpi_mapping")}
                      value={row.description}
                      onSave={(v) =>
                        patch(row.id, {
                          description: v as string | null,
                        })
                      }
                    />
                  </>
                )}
                {(kind === "kpi" ||
                  kind === "certificate" ||
                  kind === "test") && (
                  <EditableField
                    label={
                      kind === "kpi"
                        ? t("app.jcs_field_behavioral")
                        : t("app.jcs_field_requirement")
                    }
                    value={row.description}
                    kind="textarea"
                    onSave={(v) =>
                      patch(row.id, { description: v as string | null })
                    }
                  />
                )}
                {kind === "objective" && (
                  <>
                    <EditableField
                      label={t("app.jcs_field_weight")}
                      value={row.weight}
                      kind="number"
                      onSave={(v) =>
                        patch(row.id, {
                          weight: v == null ? null : String(v),
                        })
                      }
                    />
                    <EditableField
                      label={t("app.jcs_field_measurement")}
                      value={row.measurement}
                      kind="textarea"
                      onSave={(v) =>
                        patch(row.id, {
                          measurement: v as string | null,
                        })
                      }
                    />
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => deleteM.mutate(row.id)}
                aria-label={t("app.jcs_delete_aria", { name: row.name_en })}
                className="mt-5 rounded p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
